import React, { useEffect } from 'react';
import Rater from 'react-rater';
import { shallowEqual, useSelector } from 'react-redux';
import { useRouteMatch, Link } from 'react-router-dom';
import { get, shuffle } from 'lodash';
import { RootState } from 'modules';
import moment from 'moment';
import { BsFillBookmarkFill } from 'react-icons/bs';
import { DisplayName } from 'components/base/DisplayName';
import OrderingButton from 'components/base/OrderingButton';
import { ProfileWrapper, EducationWrapper } from 'components/mentoring/styles';
import { getMentoringReviews } from 'libs/api/mentoring';
import { setInterestedMentor, getMentor } from 'libs/api/user';
import { useDataApi, usePagination } from 'libs/hooks';
import { TMentoringReview } from 'modules/mentoring';
import { RouteMatch, TListResponse, TPagination } from 'modules/types';
import { TMentor, TEducation } from 'modules/user';
import { CWindowWidth } from 'constants/base.const';
import { CDegreeType } from 'constants/user.const';
import { EUserType } from 'enums/base.enum';
import { ERoute } from 'enums/route.enum';
import { Avatar, Tabs, Button, Col, Row, Typography, Divider, Spin, Tag, List } from 'antd';
import { CheckOutlined, LikeFilled } from '@ant-design/icons';
import styled from 'styled-components';
import { SecondaryColor, PrimaryColor } from 'GlobalStyles';
import IconBag from 'assets/icons/bag2.svg';
import IconEdit from 'assets/icons/edit.svg';
import IconGraduationCap from 'assets/icons/graduation_cap.svg';
import IconIntroduction from 'assets/icons/introduction.svg';
import blurPage from 'assets/images/pages/community/blurImg.png';

const { TabPane } = Tabs;
const { Text, Title } = Typography;
const MentoringMentorViewWrapper = styled.div`
  font-size: 16px;
  color: #000;

  .small-btn {
    margin: 10px 5px;
    padding: 0px 12px !important;
  }
  .ant-divider {
    margin: 35px 0px !important;
  }
  .icon {
    margin-bottom: 5px;
    margin-right: 7px;
  }

  .card {
    /* Add shadows to create the "card" effect */
    box-shadow: 0 4px 8px 0 rgba(0,0,0,0.2);
    transition: 0.3s;
    position: absolute;
    top: 55%;
    left: 50%;
    transform: translate(-50%, -50%);
    padding: 40px 10px;
  }
`;

const MentoringMentorView = () => {
  const match = useRouteMatch(`${ERoute.MentoringMentorView}`);
  const { record_id } = (match?.params as RouteMatch) || {};
  const getPromise = getMentor.bind(null, record_id);
  const [{ data, loading }, setCallback] = useDataApi<TMentor>(getPromise);
  const currentUser = useSelector((state: RootState) => state.user.current_user, shallowEqual);
  const isMentee = currentUser && get(currentUser, 'user_type') === EUserType.Mentee ? true : false;
  const mentorTagColor = get(data, 'mentor_type') === 'senior' ? `${PrimaryColor}` : `${SecondaryColor}`;

  // 멘토링 리뷰 관련
  const [pagination, setPagination] = usePagination(8);
  const reloadPage = (page?: Partial<TPagination>) => {
    setPagination({
      ...pagination,
      ...page,
    });
  };
  const getPromiseReviews = getMentoringReviews.bind(null, record_id, {
    params: {
      current: pagination.current,
      pageSize: pagination.pageSize,
      ordering: pagination.ordering,
    },
  });
  const [{ data: reviews, loading: reviewsLoading }, setCallbackReviews] = useDataApi<
    TListResponse<TMentoringReview>
  >(getPromiseReviews, {
    contents: [],
    last: false,
  });

  useEffect(() => {
    setCallbackReviews(() => getPromiseReviews);
  }, [pagination]);

  async function handleSetInterested(isInterested: boolean) {
    try {
      await setInterestedMentor({
        mentor_id: record_id,
        is_interested: isInterested,
      });
      setCallback(() => getPromise);
    } catch (e) {
      throw e;
    }
  }

  const tags = []
  const mentoring_tags = get(data, 'mentoring_tags', [])
  for (let index = 0; index < mentoring_tags.length; index++) {
    tags.push(
      <Button
        size='small'
        type='link'
        style={{ fontSize: '13px', marginLeft: '5px', marginRight: '5px', marginTop: '10px' }}
        icon={<LikeFilled />}
        >
          { mentoring_tags[index] }
      </Button>
    );
  }

  return (
    <MentoringMentorViewWrapper>
      <Spin tip='loading...' spinning={loading} delay={1000}>
        <Row
          style={{
            maxWidth: `${CWindowWidth - 300}px`,
            margin: '20px auto',
            padding: '30px 10px',
          }}
          gutter={[32, 16]}
        >
          <Col xs={24} sm={24} md={6} lg={6} xl={6} style={{ textAlign: 'center' }}>
            <ProfileWrapper style={{ maxWidth: '250px' }}>
              <Avatar className='no-hover' src={get(data, 'user.profile_image.absolute_url')} />
              {get(data, 'is_current', null) != null && (
                <Tag className='user-type-tag' color={mentorTagColor}>
                  {get(data, 'mentor_type') === 'junior' ? '주니어' : '시니어'}
                </Tag>
              )}
              <div className='name'>{get(data, 'user.fullname')}</div>
              <div className='intro'>"{get(data, 'short_introduction')}"</div>
              <div className='score' style={{ marginTop: '5px' }}>
                멘토링 횟수 {get(data, 'mentoring_count', 0)}회 <br />
                리뷰 수 {get(data, 'num_of_rater', 0)}개
              </div>
              { currentUser?.user_type === EUserType.Mentee ? 
                <div>
                  <Rater interactive={false} rating={get(data, 'score_average')} />
                  &nbsp;&nbsp;
                  <span className='score'>{get(data, 'score_average')}</span>
                </div>
                :
                <div className='score' style={{ marginTop: '5px' }}>
                </div>
              }
            </ProfileWrapper>
            <div style={{ margin: '10px 40px', textAlign: 'left' }}>
              { tags }
            </div>
            {isMentee === true && (
              <>
                <Link to={`${ERoute.MentoringClassCreate.replace(':record_id', String(record_id))}`}>
                  <Button className='small-btn' type='primary'>
                    멘토링 신청
                  </Button>
                </Link>
                {get(data, 'is_interested') === true ? (
                  <Button onClick={handleSetInterested.bind(null, false)} className='small-btn'>
                    <CheckOutlined />
                    찜완료
                  </Button>
                ) : (
                  <Button onClick={handleSetInterested.bind(null, true)} className='small-btn'>
                    <BsFillBookmarkFill size='12px' />
                    &nbsp;찜하기
                  </Button>
                )}
              </>
            )}
          </Col>
          <Col xs={24} sm={24} md={18} lg={18} xl={18}>
            <Title level={4}>
              <img alt='' className='icon' src={IconIntroduction} />
              소개
            </Title>
            <div style={{ whiteSpace: 'pre-wrap', marginBottom: '30px' }}>{get(data, 'introduction')}</div>
            <Divider />
            <Title level={4}>
              <img alt='' className='icon' src={IconGraduationCap} />
              학력
            </Title>
            <EducationWrapper style={{ marginBottom: '30px' }}>
              <List
                split={false}
                dataSource={get(data, 'user.educations')}
                renderItem={(item: TEducation) => (
                  <List.Item>
                    <List.Item.Meta
                      title={<div className='title'>{DisplayName(get(item, 'university_name'))}</div>}
                      avatar={
                        get(item, 'university_logo_url') ? (
                          <img alt='university_logo' src={get(item, 'university_logo_url')} />
                        ) : (
                          <div className='empty'></div>
                        )
                      }
                      description={
                        <div className='sub-title'>
                          {get(item, 'major')}
                          <span style={{ marginLeft: '20px' }} className='extra'>
                            {CDegreeType[get(item, 'degree_type')]}
                          </span>
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            </EducationWrapper>
            <Divider />
            <Title level={4}>
              <img alt='' className='icon' src={IconBag} />
              경력
            </Title>
            <EducationWrapper style={{ marginBottom: '30px' }}>
              <List
                split={false}
                dataSource={get(data, 'user.work_experiences')}
                renderItem={(item) => (
                  <List.Item>
                    <List.Item.Meta
                      title={<div className='title'>{DisplayName(get(item, 'company_name'))}</div>}
                      avatar={
                        get(item, 'company_logo_url') ? (
                          <img alt='company_logo' src={get(item, 'company_logo_url')} />
                        ) : (
                          <div className='empty'></div>
                        )
                      }
                      description={
                        <div className='sub-title'>
                          {get(item, 'position')}
                          <span style={{ marginLeft: '20px' }} className='extra'>
                            {get(item, 'duration_display')}
                            <span style={{ marginLeft: '20px' }}>{get(item, 'status_display')}</span>
                          </span>
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            </EducationWrapper>
            <Divider />
            { currentUser?.user_type === EUserType.Mentee ? 
              <Title level={4}>
                <img alt='' className='icon' src={IconEdit} />
                수강후기
              </Title>
              :
              <Title level={4}>
                <img alt='' className='icon' src={IconEdit} />
                수강후기 ({get(data, 'num_of_rater', 0)}개)
              </Title>
            }
            { currentUser?.user_type === EUserType.Mentee ? 
              <Row justify='space-between'>
                { /* <Col>
                    <ProfileWrapper>
                      <div style={{ lineHeight: '30px' }}>
                        <span className='score'>{get(data, 'score_average')}</span>
                        <span style={{ marginRight: '5px' }} className='num-of-rater'>
                          &nbsp;({get(data, 'num_of_rater', 0)})
                        </span>
                        <Rater interactive={false} rating={get(data, 'score_average')} />
                      </div>
                    </ProfileWrapper>
                  </Col>*/
                }
                { /*<Col>
                  <OrderingButton
                    pagination={pagination}
                    reloadPage={reloadPage}
                    defaultKey='-created_at'
                    orderingList={{
                      '-created_at': '최신순',
                      '-score': '평점 높은순',
                      score: '평점 낮은순',
                    }}
                  />
                  </Col>*/ }
              </Row>
              :
              <div></div>
            }
            {
              get(data, 'num_of_rater', 0) < 5 ? 
              <img src={blurPage} alt=''  /> 
              :
              <List
              split={false}
              dataSource={ pagination.current === 1 ? shuffle(get(reviews, 'contents', [])) : get(reviews, 'contents', [])}
              pagination={{
                ...pagination,
                total: get(reviews, 'total', 0),
                position: 'bottom',
                hideOnSinglePage: true,
                onChange: (page) => {
                  reloadPage({
                    current: page,
                    pageSize: pagination.pageSize,
                  });
                },
              }}
              renderItem={(item) => (
                <List.Item>
                  <List.Item.Meta
                    title={<div className='title'>{get(item, 'comment')}</div>}
                    avatar={<img width='40px' alt='logo' src='https://bigstep-backend-media.s3.ap-northeast-2.amazonaws.com/media/image/2020-11-18/asqziyxodp64KoMS5wbmc.png' />}
                  />
                </List.Item>
              )}
            />
            }
          </Col>
        </Row>
      </Spin>
    </MentoringMentorViewWrapper>
  );
};

export default MentoringMentorView;
